"use client";
import { twMerge } from "tailwind-merge";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps } from "../DataTable.types";
import { getCellContainerClasses, getSkeletonSize } from "./cellUtils";

/**
 * Digits in the largest ordinal a rendering will produce.
 *
 * `Math.max(1, …)` rather than trusting the arithmetic: a zero or negative count
 * would give `String(0).length === 1` by luck and `String(-1).length === 2` by
 * accident, and neither is a width.
 */
function digitsFor(largest: number): number {
  return Math.max(1, String(Math.max(1, Math.trunc(largest))).length);
}

export interface IndexCellProps<T = any> extends CellComponentProps<T> {
  /**
   * Offset added to the context's `rowIndex`, so page 2 continues rather than
   * restarting at 1. Defaults to 1 — plain 1-based numbering.
   */
  startAt?: number;
  /**
   * Zero-pad to this width. Defaults to the digit count of the largest ordinal
   * *this rendering* produces — see {@link rowCount}.
   */
  padTo?: number;
  /**
   * How many rows the table is rendering.
   *
   * The one thing a cell cannot see and the default padding needs: the width has
   * to be a property of the column, not of the row, or the same column renders
   * `1` and `10` at different widths.
   *
   * Deliberately the row count and not a page size. They are the same number only
   * when the last page is full: a 25-per-page table whose final page holds seven
   * rows must render `51`…`57`, not `051`…`057`. The tail page is the case that
   * makes a page-size rule wrong.
   */
  rowCount?: number;
  align?: "left" | "center" | "right";
  className?: string;
}

/**
 * IndexCell
 *
 * The row's ordinal in *this rendering* — muted, monospaced, zero-padded.
 *
 * ## Ordinal only, never an ID
 *
 * That is the design inventory's rule and it is the whole specification. The
 * failure mode of a row-number column is a reader treating it as a stable handle:
 * quoting "row 14" in a ticket, or believing it survives a sort. It does not. It is
 * a position, and the styling says so — muted mono reads as chrome, not as data.
 *
 * Two structural consequences follow, and both are deliberate:
 *
 * - **There is no `index` prop.** The number comes from `context.rowIndex`, which
 *   the table owns, so an ordinal that disagrees with the order the table renders
 *   is not expressible. An `index` a caller could pass is an `index` a caller could
 *   pass wrongly, and being unable to lie is this cell's only real feature.
 * - **No padding without a width to pad to.** With neither `padTo` nor `rowCount`
 *   the number renders bare. A cell that cannot see the largest ordinal must not
 *   guess at one: `007` on a seven-row table reads as an identifier, which is
 *   precisely what the inventory forbids.
 *
 * ## Paging
 *
 * On page 2 of an offset-paginated table the ordinal restarts at 1 unless the host
 * sets `startAt`. Serializable block props carry rows, not an offset, so the block
 * cannot set it; a host rendering this directly can. Stated rather than hidden.
 */
export function IndexCell<T = any>({
  context,
  startAt = 1,
  padTo,
  rowCount,
  align = "right",
  className,
}: IndexCellProps<T>) {
  const { isLoading, rowIndex } = context;

  if (isLoading) {
    return <Skeleton className={getSkeletonSize("normal")} />;
  }

  // `CellContext` types `rowIndex` as required, so this guard is against a JS
  // caller rather than a TypeScript one — and it is worth having: without a
  // position there is no ordinal, and `NaN` in a row-number column is worse than
  // the platform's em dash.
  if (typeof rowIndex !== "number" || !Number.isFinite(rowIndex)) {
    return <div className={getCellContainerClasses(align, "text-text-muted")}>—</div>;
  }

  const ordinal = rowIndex + startAt;
  const width = padTo ?? (rowCount === undefined ? undefined : digitsFor(rowCount + startAt - 1));
  const text = width === undefined ? String(ordinal) : String(ordinal).padStart(width, "0");

  return (
    <div className={getCellContainerClasses(align, twMerge("font-mono text-text-muted tabular-nums", className))}>
      <span>{text}</span>
    </div>
  );
}
